import { queryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { createQueryKeys, queryClient, toApiError } from '../core'
import { isTerminal, taskPhase, type SseTaskDto, type SseTaskFilters, type TaskPhase } from './sse-tasks.schemas'
import { sseTasksService } from './sse-tasks.service'

export const sseTasksKeys = createQueryKeys('sse-tasks')

export const sseTasksQueries = {
  all: (filters?: SseTaskFilters) => queryOptions({ queryKey: sseTasksKeys.all(filters), queryFn: () => sseTasksService.listAll(filters) }),
  detail: (id: string) => queryOptions({ queryKey: sseTasksKeys.detail(id), queryFn: () => sseTasksService.get(id) }),
}

/**
 * A project's background tasks, newest first, asked for ONCE (no polling, no refetch on focus or mount). A task that is running is followed
 * through its event stream (`useTaskProgress`), never by asking for this list again.
 */
export function useProjectTasks(projectId: string | undefined) {
  return useQuery({
    ...sseTasksQueries.all({ projectId }),
    enabled: Boolean(projectId),
    select: (tasks) => tasks.toSorted((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

const openTasksKey = (projectId: string | undefined) => [...sseTasksKeys.all({ projectId }), 'open'] as const
const loadOpenTasks = (projectId: string | undefined, signal?: AbortSignal) =>
  // One request, one page: a project's open tasks are among its latest.
  sseTasksService.list({ projectId, page: 1, pageSize: 100 }, signal).then((page) => page.items)

/** How long the project's task list is trusted. It is asked for when a page opens and when the window is focused again after this long: never on a timer. */
const OPEN_TASKS_TRUST_MS = 60_000

/**
 * The tasks of one kind that a project has going on (waiting or running), whoever started them. The page that started a task follows it by
 * its id; this is how everyone else on the same project learns that one exists. The list is asked for once when the page opens (and again
 * when the window is focused after a minute away), never on a timer: a task found here is followed through its own event stream
 * (`useTaskProgress`), which says when it is done. Anything started after that is found by `refreshOpenProjectTasks`, which a page calls
 * just before it lets someone start a task of their own.
 */
export function useOpenProjectTasks(projectId: string | undefined, kind: (task: SseTaskDto) => boolean, enabled = true) {
  return useQuery({
    queryKey: openTasksKey(projectId),
    queryFn: ({ signal }) => loadOpenTasks(projectId, signal),
    enabled: Boolean(projectId) && enabled,
    select: (tasks) => tasks.filter((task) => kind(task) && !isTerminal(taskPhase(task))).toSorted((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    staleTime: OPEN_TASKS_TRUST_MS,
    gcTime: 0,
  })
}

/** Asks the server for the project's tasks now, puts the answer where `useOpenProjectTasks` reads it, and returns it. Used before starting a task, to be sure none is running. */
export function refreshOpenProjectTasks(projectId: string) {
  return queryClient.fetchQuery({ queryKey: openTasksKey(projectId), queryFn: ({ signal }) => loadOpenTasks(projectId, signal), staleTime: 0 })
}

export interface TaskProgress {
  task: SseTaskDto | null
  phase: TaskPhase
  /** 0 to 100. */
  progress: number
  errorMessage: string | null
  /** Every distinct event the task has pushed, oldest first: what the live timeline of a variation is drawn from. */
  events: TaskEvent[]
  /** True while the live stream is delivering; false when the app has fallen back to asking every couple of seconds. */
  live: boolean
}

/** One thing the server said about a task. */
export interface TaskEvent {
  at: number
  phase: TaskPhase
  progress: number
  /** The server's own words (`stage`, else `message`), when a frame has any. */
  text: string | null
}

const eventOf = (task: SseTaskDto): TaskEvent => ({ at: Date.now(), phase: taskPhase(task), progress: Math.max(0, Math.min(100, Math.round(task.progress ?? 0))), text: task.stage?.trim() || task.message?.trim() || null })
const sameEvent = (a: TaskEvent | undefined, b: TaskEvent) => Boolean(a) && a!.phase === b.phase && a!.progress === b.progress && a!.text === b.text

const newer = (a: SseTaskDto | null, b: SseTaskDto | null) => (!a ? b : !b ? a : (b.updatedAt ?? '') >= (a.updatedAt ?? '') ? b : a)

/**
 * Follows one background task. It listens to the server's event stream, so progress appears as it happens; if the stream cannot be
 * opened or ends before the task is done, it quietly falls back to asking for the task every two seconds. Either way the caller
 * gets one answer: the task, its phase and its progress.
 */
export function useTaskProgress(taskId: string | null | undefined): TaskProgress {
  const [pushed, setPushed] = useState<{ taskId: string; task: SseTaskDto } | null>(null)
  const [ended, setEnded] = useState<string | null>(null)
  const [log, setLog] = useState<{ taskId: string; events: TaskEvent[] }>({ taskId: '', events: [] })
  const record = (task: SseTaskDto) => setLog((current) => {
    const event = eventOf(task)
    const events = current.taskId === task.id ? current.events : []
    return sameEvent(events.at(-1), event) ? current : { taskId: task.id, events: [...events, event] }
  })

  useEffect(() => {
    if (!taskId) return
    const controller = new AbortController()
    void (async () => {
      try {
        for await (const update of sseTasksService.stream(taskId, controller.signal)) { setPushed({ taskId, task: update }); record(update) }
      } catch (error) {
        if (controller.signal.aborted || toApiError(error).isCancelled) return
      }
      if (!controller.signal.aborted) setEnded(taskId)
    })()
    return () => controller.abort()
  }, [taskId])

  const fromStream = pushed && pushed.taskId === taskId ? pushed.task : null
  const streamDone = ended === taskId
  const finished = isTerminal(taskPhase(fromStream))
  // Ask for the task directly only when the stream is not going to tell us: it ended or failed before the task finished.
  const polled = useQuery({
    ...sseTasksQueries.detail(taskId ?? ''),
    enabled: Boolean(taskId) && streamDone && !finished,
    refetchInterval: (query) => (isTerminal(taskPhase(query.state.data)) ? false : 2000),
    staleTime: 0,
  })

  useEffect(() => {
    if (polled.data) record(polled.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polled.data])

  const task = newer(fromStream, polled.data ?? null)
  const phase = taskPhase(task)
  return {
    task,
    phase,
    progress: phase === 'done' ? 100 : Math.max(0, Math.min(100, task?.progress ?? 0)),
    events: log.taskId === taskId ? log.events : [],
    errorMessage: task?.errorMessage ?? (polled.error ? toApiError(polled.error).message : null),
    live: Boolean(taskId) && !streamDone && !finished,
  }
}
